"use client"

import { useState } from "react"
import { Upload } from "lucide-react"
import {
  Dialog,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/Dialog"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectPopup,
  SelectItem,
} from "@/components/ui/Select"
import { Button } from "@/components/ui/Button"
import { useUploadDocument } from "@/contexts/UploadDocumentContext"
import { useDemoDealsDocuments } from "@/hooks/deals/useDemoDealsDocuments"
import { DocumentSelector } from "./DocumentSelector"

export function UploadDocumentDialog() {
  const {
    isOpen,
    selectedDealId,
    droppedFiles,
    closeDialog,
    clearFiles,
  } = useUploadDocument()
  const { demoDeals } = useDemoDealsDocuments()

  const [dealId, setDealId] = useState<string | undefined>(selectedDealId ?? undefined)
  const [files, setFiles] = useState<File[]>(droppedFiles)
  const [selectedContracts, setSelectedContracts] = useState<string[]>([])
  const [isUploading, setIsUploading] = useState(false)

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setDealId(selectedDealId ?? undefined)
      setFiles(droppedFiles)
      setSelectedContracts([])
    } else {
      closeDialog()
      setFiles([])
      setDealId(undefined)
      setSelectedContracts([])
    }
  }

  const handleUpload = async () => {
    const hasContent = files.length > 0 || selectedContracts.length > 0
    if (!hasContent || !dealId) return

    setIsUploading(true)

    // Simulate upload/linking
    await new Promise(resolve => setTimeout(resolve, 1500))

    setIsUploading(false)
    closeDialog()
    clearFiles()
    setFiles([])
    setDealId(undefined)
    setSelectedContracts([])
  }

  const totalItems = files.length + selectedContracts.length

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPopup className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Documents to Deal</DialogTitle>
          <DialogDescription>
            Upload new files or select existing contracts from your vault.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Deal Select */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Select Deal
            </label>
            <Select value={dealId} onValueChange={(value) => setDealId(value || undefined)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectPopup>
                {demoDeals.map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>
                    <span className="flex items-center gap-2">
                      <span className="truncate">{deal.name}</span>
                      <span className="text-muted-foreground text-xs">
                        {deal.type}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          </div>

          {/* Document Selector */}
          <DocumentSelector
            files={files}
            selectedContracts={selectedContracts}
            onFilesChange={setFiles}
            onContractsChange={setSelectedContracts}
            compact
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={totalItems === 0 || !dealId || isUploading}
          >
            {isUploading ? (
              <>
                <span className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Adding…
              </>
            ) : (
              <>
                <Upload className="size-4" />
                Add to Deal {totalItems > 0 ? `(${totalItems})` : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  )
}









